import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AdminSidebar.css';

/* Inline SVG icons (20x20, stroke-based, strokeWidth 2) */
const icons = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  subscriptions: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  transactions: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  revenue: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  churn: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  learningTime: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  creators: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  email: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  auditLog: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  ),
  toggle: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
};

interface Props {
  isOpen: boolean;
  onToggle: () => void;
}

const AdminSidebar: React.FC<Props> = ({ isOpen, onToggle }) => {
  const { t } = useTranslation('admin');

  const links = [
    { to: '/admin', label: t('sidebar.dashboard', 'Dashboard'), icon: icons.dashboard, end: true },
    { to: '/admin/subscriptions', label: t('sidebar.subscriptions', 'Subscriptions'), icon: icons.subscriptions },
    { to: '/admin/transactions', label: t('sidebar.transactions', 'Transactions'), icon: icons.transactions },
    { to: '/admin/revenue', label: t('sidebar.revenue', 'Revenue'), icon: icons.revenue },
    { to: '/admin/users', label: t('sidebar.users', 'Users'), icon: icons.users },
    { to: '/admin/churn', label: t('sidebar.churn', 'Churn & Retention'), icon: icons.churn },
    // -- separator after index 5 --
    { to: '/admin/learning-time', label: t('sidebar.learningTime', 'Learning Time'), icon: icons.learningTime },
    { to: '/admin/creator-contributions', label: t('sidebar.creators', 'Creator Stats'), icon: icons.creators },
    { to: '/admin/email-hub', label: t('sidebar.email', 'Email Hub'), icon: icons.email },
    { to: '/admin/audit-log', label: t('sidebar.auditLog', 'Audit Log'), icon: icons.auditLog },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && <div className="admin-sidebar-backdrop" onClick={onToggle} />}

      <aside className={`admin-sidebar ${isOpen ? '' : 'admin-sidebar--collapsed'}`}>
        <div className="admin-sidebar-header">
          <span className="admin-sidebar-title">{isOpen ? 'Loopy Admin' : 'LA'}</span>
          <button className="admin-sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
            {icons.toggle}
          </button>
        </div>
        <nav className="admin-sidebar-nav">
          {links.map((link, i) => (
            <React.Fragment key={link.to}>
              {i === 6 && <div className="admin-sidebar-divider" />}
              <NavLink
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `admin-sidebar-link ${isActive ? 'admin-sidebar-link--active' : ''}`
                }
              >
                <span className="admin-sidebar-icon">{link.icon}</span>
                {isOpen && <span className="admin-sidebar-label">{link.label}</span>}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
